import { Link } from "@inertiajs/react"

interface Option {
  id: number
  name: string
}

interface LocationFormData {
  name: string
  address: string
  address2: string
  city: string
  state: string
  zip: string
  phone: string
  fax: string
  email: string
  website: string
  notes: string
  sales_rep_id: number | null
}

interface Props {
  customerId: number
  data: LocationFormData
  setData: (key: keyof LocationFormData, value: string | number | null) => void
  errors?: Record<string, string[]>
  processing: boolean
  onSubmit: (e: React.FormEvent) => void
  submitLabel: string
  users: Option[]
}

export default function LocationForm({
  customerId,
  data,
  setData,
  errors,
  processing,
  onSubmit,
  submitLabel,
  users,
}: Props) {
  return (
    <form onSubmit={onSubmit} className="max-w-2xl">
      <div className="mb-4">
        <label htmlFor="name" className="mb-1 block font-medium">
          Location Name
        </label>
        <input
          type="text"
          id="name"
          value={data.name}
          onChange={(e) => setData("name", e.target.value)}
          className="border-hpi-light-gray w-full rounded border px-3 py-2"
          maxLength={100}
        />
        {errors?.name && (
          <p className="text-hpi-red mt-1 text-sm">{errors.name.join(", ")}</p>
        )}
      </div>

      <div className="mb-4">
        <label htmlFor="address" className="mb-1 block font-medium">
          Address
        </label>
        <input
          type="text"
          id="address"
          value={data.address}
          onChange={(e) => setData("address", e.target.value)}
          className="border-hpi-light-gray w-full rounded border px-3 py-2"
          maxLength={100}
        />
      </div>

      <div className="mb-4">
        <label htmlFor="address2" className="mb-1 block font-medium">
          Address 2
        </label>
        <input
          type="text"
          id="address2"
          value={data.address2}
          onChange={(e) => setData("address2", e.target.value)}
          className="border-hpi-light-gray w-full rounded border px-3 py-2"
          maxLength={100}
        />
      </div>

      <div className="mb-4 grid gap-4 md:grid-cols-3">
        <div>
          <label htmlFor="city" className="mb-1 block font-medium">
            City
          </label>
          <input
            type="text"
            id="city"
            value={data.city}
            onChange={(e) => setData("city", e.target.value)}
            className="border-hpi-light-gray w-full rounded border px-3 py-2"
            maxLength={50}
          />
        </div>
        <div>
          <label htmlFor="state" className="mb-1 block font-medium">
            State
          </label>
          <input
            type="text"
            id="state"
            value={data.state}
            onChange={(e) => setData("state", e.target.value)}
            className="border-hpi-light-gray w-full rounded border px-3 py-2"
            maxLength={50}
          />
        </div>
        <div>
          <label htmlFor="zip" className="mb-1 block font-medium">
            Zip
          </label>
          <input
            type="text"
            id="zip"
            value={data.zip}
            onChange={(e) => setData("zip", e.target.value)}
            className="border-hpi-light-gray w-full rounded border px-3 py-2"
            maxLength={20}
          />
        </div>
      </div>

      <div className="mb-4 grid gap-4 md:grid-cols-2">
        <div>
          <label htmlFor="phone" className="mb-1 block font-medium">
            Phone
          </label>
          <input
            type="tel"
            id="phone"
            value={data.phone}
            onChange={(e) => setData("phone", e.target.value)}
            className="border-hpi-light-gray w-full rounded border px-3 py-2"
            maxLength={50}
          />
        </div>
        <div>
          <label htmlFor="fax" className="mb-1 block font-medium">
            Fax
          </label>
          <input
            type="tel"
            id="fax"
            value={data.fax}
            onChange={(e) => setData("fax", e.target.value)}
            className="border-hpi-light-gray w-full rounded border px-3 py-2"
            maxLength={50}
          />
        </div>
      </div>

      <div className="mb-4 grid gap-4 md:grid-cols-2">
        <div>
          <label htmlFor="email" className="mb-1 block font-medium">
            Email
          </label>
          <input
            type="email"
            id="email"
            value={data.email}
            onChange={(e) => setData("email", e.target.value)}
            className="border-hpi-light-gray w-full rounded border px-3 py-2"
            maxLength={100}
          />
        </div>
        <div>
          <label htmlFor="website" className="mb-1 block font-medium">
            Website
          </label>
          <input
            type="url"
            id="website"
            value={data.website}
            onChange={(e) => setData("website", e.target.value)}
            className="border-hpi-light-gray w-full rounded border px-3 py-2"
            maxLength={100}
          />
        </div>
      </div>

      <div className="mb-4">
        <label htmlFor="sales_rep_id" className="mb-1 block font-medium">
          Sales Rep
        </label>
        <select
          id="sales_rep_id"
          value={data.sales_rep_id ?? ""}
          onChange={(e) =>
            setData(
              "sales_rep_id",
              e.target.value ? parseInt(e.target.value) : null,
            )
          }
          className="border-hpi-light-gray w-full rounded border px-3 py-2"
        >
          <option value="">Select...</option>
          {users.map((u) => (
            <option key={u.id} value={u.id}>
              {u.name}
            </option>
          ))}
        </select>
      </div>

      <div className="mb-4">
        <label htmlFor="notes" className="mb-1 block font-medium">
          Notes
        </label>
        <textarea
          id="notes"
          value={data.notes}
          onChange={(e) => setData("notes", e.target.value)}
          className="border-hpi-light-gray w-full rounded border px-3 py-2"
          rows={3}
        />
      </div>

      <div className="flex gap-2">
        <button
          type="submit"
          disabled={processing}
          className="bg-hpi-dark-blue hover:bg-hpi-blue rounded px-4 py-2 text-white disabled:opacity-50"
        >
          {submitLabel}
        </button>
        <Link
          href={`/customers/${customerId}/locations`}
          className="border-hpi-light-gray hover:bg-hpi-pale-blue rounded border px-4 py-2"
        >
          Cancel
        </Link>
      </div>
    </form>
  )
}
